import React, {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Container} from 'react-bootstrap';
import {Link} from 'react-router-dom';
import {DateTime} from 'luxon';
import ReactSelect, {components, OptionProps} from 'react-select';
import {ColumnDef, PaginationState} from '@tanstack/react-table';
import {PageTitle} from '../../../_metronic/layout/core';
import TableMetronic from '../../../app/common/components/table/TableMetronic';
import {DivopsAccountFilterOption, DivopsApsHistoryQueryParams, DivopsApsRequestEntry} from '../Types/types';
import {useGetApsRequests, useGetApsRequestsFilters} from '../Hooks/DivopsHooks';

interface FilterOption {
  value: string;
  label: string;
}

const filterSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
};

const filterDateInputStyle = {height: 38, fontSize: '0.96rem', minWidth: 140};

const filterDateAddonStyle = {height: 38, fontSize: '0.85rem'};

const CheckboxOption = (props: OptionProps<FilterOption, true>) => (
  <components.Option {...props}>
    <div className="d-flex align-items-start">
      <input type="checkbox" checked={props.isSelected} onChange={() => null} className="me-2 mt-1 flex-shrink-0" />
      <label className="mb-0 flex-grow-1" style={{wordBreak: 'break-word'}}>
        {props.label}
      </label>
    </div>
  </components.Option>
);

const toOptions = (values: string[] = []): FilterOption[] => values.map((value) => ({value, label: value}));

const toAccountOptions = (accounts: DivopsAccountFilterOption[] = []): FilterOption[] =>
  accounts.map(({id, name}) => ({value: id, label: name ? `${id} - ${name}` : id}));

const ApsHistoryPage = (): JSX.Element => {
  const intl = useIntl();

  const [selectedAccounts, setSelectedAccounts] = useState<FilterOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<FilterOption[]>([]);
  const [exactDate, setExactDate] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [paginationState, setPaginationState] = useState<PaginationState>({pageIndex: 0, pageSize: 10});

  useEffect(() => {
    setPaginationState((prev) => (prev.pageIndex === 0 ? prev : {...prev, pageIndex: 0}));
  }, [selectedAccounts, selectedUsers, exactDate, dateFrom, dateTo]);

  const {data: filters} = useGetApsRequestsFilters();
  const accountOptions = useMemo(() => toAccountOptions(filters?.accounts), [filters]);
  const userOptions = useMemo(() => toOptions(filters?.users), [filters]);

  const queryParams: DivopsApsHistoryQueryParams = useMemo(
    () => ({
      page: paginationState.pageIndex + 1,
      perPage: paginationState.pageSize,
      accountIds: selectedAccounts.length ? selectedAccounts.map((o) => o.value) : undefined,
      userEmails: selectedUsers.length ? selectedUsers.map((o) => o.value) : undefined,
      dateExact: exactDate || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [paginationState, selectedAccounts, selectedUsers, exactDate, dateFrom, dateTo]
  );

  const {data: response, isLoading, isFetching} = useGetApsRequests(queryParams);

  const columns = useMemo<ColumnDef<DivopsApsRequestEntry>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'DIVOPS.HISTORY.COL.DATE'}),
        accessorKey: 'createdAt',
        cell: (info) => DateTime.fromISO(info.getValue<string>()).toFormat('dd/MM/yyyy HH:mm'),
        size: 150,
        enableSorting: false,
      },
      {
        header: intl.formatMessage({id: 'DIVOPS.HISTORY.COL.ADRESSE'}),
        accessorKey: 'adresse',
        cell: (info) => info.getValue<string | null>() ?? '-',
        enableSorting: false,
      },
      {
        header: intl.formatMessage({id: 'DIVOPS.APS.SERVICE'}),
        accessorKey: 'serviceSouhaite',
        enableSorting: false,
      },
      {
        header: intl.formatMessage({id: 'DIVOPS.APS.TYPE'}),
        accessorKey: 'type',
        enableSorting: false,
      },
      {
        header: intl.formatMessage({id: 'DIVOPS.APS.DUREE'}),
        accessorKey: 'duree',
        enableSorting: false,
      },
      {
        header: intl.formatMessage({id: 'DIVOPS.APS.BUDGET'}),
        accessorKey: 'budget',
        cell: (info) => `${info.getValue<string>()} EUR`,
        enableSorting: false,
      },
      {
        header: intl.formatMessage({id: 'DIVOPS.APS.HISTORY.COL.TICKET'}),
        accessorKey: 'ticketNumber',
        cell: (info) => info.getValue<string | null>() ?? '-',
        size: 120,
        enableSorting: false,
      },
      {
        header: intl.formatMessage({id: 'DIVOPS.HISTORY.COL.EMAIL'}),
        accessorKey: 'userEmail',
        enableSorting: false,
      },
      {
        header: intl.formatMessage({id: 'DIVOPS.HISTORY.COL.ACCOUNT_ID'}),
        accessorKey: 'accountId',
        size: 100,
        enableSorting: false,
      },
    ],
    [intl]
  );

  const filtersToolbar = (
    <>
      <div style={{minWidth: 220, flex: 1, maxWidth: 320}}>
        <ReactSelect<FilterOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={accountOptions}
          value={selectedAccounts}
          onChange={(vals) => setSelectedAccounts(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'DIVOPS.HISTORY.FILTER.ALL_ACCOUNTS'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
      <div style={{minWidth: 220, flex: 1, maxWidth: 320}}>
        <ReactSelect<FilterOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={userOptions}
          value={selectedUsers}
          onChange={(vals) => setSelectedUsers(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'DIVOPS.HISTORY.FILTER.ALL_USERS'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
      <div className="input-group" style={{width: 'auto'}}>
        <span className="input-group-text" style={filterDateAddonStyle}>
          {intl.formatMessage({id: 'DIVOPS.HISTORY.FILTER.DATE_EXACT'})}
        </span>
        <input
          type="date"
          className="form-control"
          style={filterDateInputStyle}
          value={exactDate}
          onChange={(e) => setExactDate(e.target.value)}
        />
      </div>
      <div className="input-group" style={{width: 'auto'}}>
        <span className="input-group-text" style={filterDateAddonStyle}>
          {intl.formatMessage({id: 'DIVOPS.HISTORY.FILTER.DATE_FROM'})}
        </span>
        <input
          type="date"
          className="form-control"
          style={filterDateInputStyle}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
      </div>
      <div className="input-group" style={{width: 'auto'}}>
        <span className="input-group-text" style={filterDateAddonStyle}>
          {intl.formatMessage({id: 'DIVOPS.HISTORY.FILTER.DATE_TO'})}
        </span>
        <input
          type="date"
          className="form-control"
          style={filterDateInputStyle}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />
      </div>
    </>
  );

  return (
    <Container fluid className="px-0">
      <PageTitle
        breadcrumbs={[
          {title: 'DivOps', path: '/divops/aps', isSeparator: false, isActive: false},
        ]}
      >
        {intl.formatMessage({id: 'DIVOPS.APS.HISTORY.PAGE_TITLE'})}
      </PageTitle>

      <div className="card mx-5">
        <div className="card-body py-10">
          <div className="d-flex justify-content-end mb-6">
            <Link to="/divops/aps" className="btn btn-light-primary">
              <i className="fas fa-arrow-left me-2"></i>
              {intl.formatMessage({id: 'DIVOPS.HISTORY.BACK'})}
            </Link>
          </div>

          <TableMetronic<DivopsApsRequestEntry>
            toolbar={filtersToolbar}
            stackedHeader
            searchBar={false}
            data={response?.data ?? []}
            columns={columns}
            isLoading={isLoading || isFetching}
            pagination={true}
            manualPagination
            paginationState={paginationState}
            onPaginationChange={setPaginationState}
            pageCount={response?.meta.totalPages ?? 0}
            rowCount={response?.meta.total ?? 0}
            noRowsMessage={intl.formatMessage({id: 'DIVOPS.APS.HISTORY.EMPTY'})}
          />
        </div>
      </div>
    </Container>
  );
};

export default ApsHistoryPage;
